import React, { useRef, useCallback, useState, useEffect } from "react";
import EditorJS from "@editorjs/editorjs";
import { EDITOR_JS_TOOLS } from "./editor/tools/tools";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { getSignedUrl } from "../../api/documents";

export default function Editor({
  data,
  setData,
  onSave,
  showControls = true,
  readOnly = false,
  blogStatus,
  setBlogStatus,
  setIsInCarousel,
  isInCarousel,
  countryCodes = [],
  setCountryCodes,
  categoryIds = [],
  setCategoryIds,
  allCategories = [],
  allData,
  backToListPath = "/dashboard/blogs",
}) {
  const navigate = useNavigate();
  const editorCore = useRef(null);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [processedData, setProcessedData] = useState(null);

  const processImageUrls = async (editorData) => {
    if (!editorData || !editorData.blocks) return editorData;

    const processedBlocks = await Promise.all(
      editorData.blocks.map(async (block) => {
        // Ensure linkTool blocks are preserved properly
        if (block.type === "linkTool" && block.data) {
          // Return linkTool blocks as-is to preserve their structure
          return block;
        }

        // Process image blocks
        // if (block.type === "image" && block.data && block.data.file.url) {
        //   const imageUrl = block.data.file.url;
        //   console.log("Image URL:", imageUrl);
        //   // If it's already a full URL, try to extract and store the original path
        //   if (
        //     imageUrl &&
        //     (imageUrl.startsWith("http://") || imageUrl.startsWith("https://"))
        //   ) {
        //     // Try to extract the path from signed URL to store it
        //     const urlMatch =
        //       imageUrl.match(/\/uploads\/[^?]+/) ||
        //       imageUrl.match(/\/blogs\/[^?]+/);
        //     if (urlMatch) {
        //       const extractedPath = urlMatch[0].startsWith("/")
        //         ? urlMatch[0].substring(1)
        //         : urlMatch[0];
        //       return {
        //         ...block,
        //         data: {
        //           ...block.data,
        //           file: {
        //             ...block.data.file,
        //             originalPath: extractedPath,
        //           },
        //         },
        //       };
        //     }
        //     // If we can't extract path, keep block as is
        //     return block;
        //   }

        //   // If it's a path, convert to signed URL
        //   try {
        //     // Handle paths with or without leading slash
        //     const filePath = imageUrl.startsWith("/")
        //       ? imageUrl.substring(1)
        //       : imageUrl;

        //     const signedUrlResponse = await getSignedUrl(filePath);
        //     const signedUrl =
        //       signedUrlResponse?.data?.url ||
        //       signedUrlResponse?.data?.signedUrl ||
        //       signedUrlResponse?.url ||
        //       signedUrlResponse?.signedUrl ||
        //       signedUrlResponse?.data;

        //     if (signedUrl && typeof signedUrl === "string") {
        //       return {
        //         ...block,
        //         data: {
        //           ...block.data,
        //           file: {
        //             url: signedUrl,
        //             // Store original path for later restoration
        //             originalPath: filePath,
        //           },
        //         },
        //       };
        //     }
        //   } catch (error) {
        //     console.error("Failed to get signed URL for image:", error);
        //     // Keep original URL if signing fails
        //   }
        // }
        return block;
      }),
    );

    return {
      ...editorData,
      blocks: processedBlocks,
    };
  };

  //   const [editorData, setEditorData] = useState(data);

  //   // Default data with a placeholder paragraph
  //   const getDefaultData = () => ({
  //     time: Date.now(),
  //     blocks: [
  //       {
  //         id: "default-paragraph",
  //         type: "paragraph",
  //         data: {
  //           text: "",
  //         },
  //       },
  //     ],
  //   });

  //   useEffect(() => {
  //     if (!data) {
  //       setEditorData(getDefaultData());
  //     } else {
  //       setEditorData(data);
  //     }
  //   }, [data]);

  // Initialize EditorJS
  useEffect(() => {
    // Use a timeout to ensure DOM is ready and data is processed
    const initEditor = async () => {
      if (!editorCore.current && containerRef.current) {
        console.log("Initializing EditorJS with data:", data);

        if (data) {
          const processed = await processImageUrls(data);
          console.log("Processed data:", processed);
          setProcessedData(processed);

          editorCore.current = new EditorJS({
            holder: containerRef.current,
            tools: EDITOR_JS_TOOLS,
            data: processed,
            readOnly: readOnly,
            placeholder: "Start writing your blog content here...",
            onChange: () => {
              setHasChanges(true);
            },
            onReady: () => {
              setIsReady(true);
              console.log("EditorJS is ready");
            },
            minHeight: 200,
          });
        } else {
          setProcessedData(null);
          editorCore.current = new EditorJS({
            holder: containerRef.current,
            tools: EDITOR_JS_TOOLS,
            data: null,
            readOnly: readOnly,
            placeholder: "Start writing your blog content here...",
            onChange: () => {
              setHasChanges(true);
            },
            onReady: () => {
              setIsReady(true);
              console.log("EditorJS is ready");
            },
            minHeight: 200,
          });
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(initEditor, 100);

    return () => {
      clearTimeout(timeoutId);
      if (editorCore.current && editorCore.current.destroy) {
        editorCore.current.destroy();
        editorCore.current = null;
        setIsReady(false);
      }
    };
  }, [readOnly, data]);

  // Update editor data when data prop changes
  //   useEffect(() => {
  //     if (editorCore.current && data && data !== editorData) {
  //       editorCore.current.render(data);
  //       setHasChanges(false);
  //     }
  //   }, [data, editorData]);

  //   useEffect(() => {
  //     if (data && data.blocks.length > 0) {
  //       editorCore.current.render(data);
  //       setHasChanges(false);
  //     }
  //   }, [data]);

  const handleSave = useCallback(async () => {
    if (!editorCore.current) return;

    try {
      // retrieve data inserted
      const savedData = await editorCore.current.save();

      // Convert signed URLs back to paths for saving
      const processedSavedData = {
        ...savedData,
        blocks: savedData.blocks.map((block) => {
          // Process image blocks - restore original path if we have it
          // if (block.type === "image" && block.data && block.data.file) {
          //   const imageUrl = block.data.file.url;

          //   // If we have the original path stored, use it instead of signed URL
          //   if (block.data.file.originalPath) {
          //     return {
          //       ...block,
          //       data: {
          //         ...block.data,
          //         file: {
          //           url: block.data.file.originalPath, // Use original path
          //         },
          //       },
          //     };
          //   }

          //   // If URL is already a path (not http/https), keep it as is
          //   if (
          //     imageUrl &&
          //     !imageUrl.startsWith("http://") &&
          //     !imageUrl.startsWith("https://")
          //   ) {
          //     return block; // Already a path, keep as is
          //   }

          //   // If it's a signed URL but we don't have originalPath, try to extract from URL
          //   // or keep it (it might be a new uploaded image with only signed URL)
          //   if (
          //     imageUrl &&
          //     (imageUrl.startsWith("http://") ||
          //       imageUrl.startsWith("https://"))
          //   ) {
          //     // Try to extract the path from signed URL (if it contains a path pattern)
          //     // This is a fallback if originalPath wasn't stored
          //     const urlMatch =
          //       imageUrl.match(/\/uploads\/[^?]+/) ||
          //       imageUrl.match(/\/blogs\/[^?]+/);
          //     if (urlMatch) {
          //       const extractedPath = urlMatch[0].startsWith("/")
          //         ? urlMatch[0].substring(1)
          //         : urlMatch[0];
          //       return {
          //         ...block,
          //         data: {
          //           ...block.data,
          //           file: {
          //             url: extractedPath,
          //           },
          //         },
          //       };
          //     }

          //     // If we can't extract path, log warning and keep the signed URL
          //     // This might happen with newly uploaded images that don't have a path yet
          //     console.warn(
          //       "Image block has signed URL but no original path stored. URL:",
          //       imageUrl
          //     );
          //   }
          // }
          return block;
        }),
      };

      // save data
      setData(processedSavedData);
      setHasChanges(false);

      // Call external save handler if provided
      if (onSave) {
        onSave(processedSavedData);
      }

      console.log("Content saved:", processedSavedData);
    } catch (error) {
      console.error("Error saving content:", error);
    }
  }, [setData, onSave]);

  // External toolbar functions - Block insertion
  const handleList = useCallback(() => {
    if (editorCore.current) {
      try {
        editorCore.current.blocks.insert("list", {
          style: "unordered",
          items: ["New list item"],
        });
      } catch (error) {
        console.error("Error inserting list:", error);
      }
    }
  }, []);

  const handleImage = useCallback(() => {
    if (editorCore.current) {
      try {
        editorCore.current.blocks.insert("image", {
          url: "",
          caption: "Image caption",
        });
      } catch (error) {
        console.error("Error inserting image:", error);
      }
    }
  }, []);

  const handleTable = useCallback(() => {
    if (editorCore.current) {
      try {
        editorCore.current.blocks.insert("table", {
          withHeadings: true,
          content: [
            ["Header 1", "Header 2", "Header 3"],
            ["Row 1 Col 1", "Row 1 Col 2", "Row 1 Col 3"],
          ],
        });
      } catch (error) {
        console.error("Error inserting table:", error);
      }
    }
  }, []);

  const handleCTA = useCallback(() => {
    if (editorCore.current) {
      try {
        editorCore.current.blocks.insert("cta", {
          text: "CTA Section will be loaded here on Blog Page",
        });
      } catch (error) {
        console.error("Error inserting CTA:", error);
      }
    }
  }, []);

  // Auto-save functionality
  //   useEffect(() => {
  //     if (!isReady) return;

  //     const autoSaveInterval = setInterval(() => {
  //       if (hasChanges) {
  //         handleSave();
  //       }
  //     }, 30000); // Auto-save every 30 seconds if there are changes

  //     return () => clearInterval(autoSaveInterval);
  //   }, [isReady, hasChanges, handleSave]);

  return (
    <div className="editor-wrapper w-full flex h-screen max-h-screen">
      {/* Left Sidebar */}
      {showControls && (
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="mb-6 px-6 relative">
              <ArrowLeftIcon
                className="h-6 w-6 cursor-pointer absolute top-0 left-0"
                onClick={() => navigate(backToListPath)}
              />{" "}
              <div className="flex flex-col justify-between ml-2">
                <h1 className="font-bold text-gray-900 mb-2 flex gap-3">
                  {allData?.title}
                </h1>
                <p className="text-gray-600">
                  {!allData?.description
                    ? "No description"
                    : allData?.description}
                </p>
              </div>
            </div>
          </div>

          {/* Save and Status Controls */}
          <div className="p-4 space-y-4">
            {/* Status Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Publication Status
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                value={blogStatus}
                onChange={(e) => setBlogStatus(e.target.value)}
              >
                <option value="DRAFT">📝 Draft</option>
                <option value="PUBLISHED">🚀 Published</option>
              </select>
            </div>

            {/* In Carousel Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Carousel Display
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                value={isInCarousel}
                onChange={(e) => setIsInCarousel(e.target.value)}
              >
                <option value={false}>❌ No</option>
                <option value={true}>✅ Yes</option>
              </select>
            </div>

            {/* Categories */}
            {typeof setCategoryIds === "function" && Array.isArray(allCategories) && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Categories
                </label>
                <p className="text-xs text-gray-500">
                  Link this blog to categories
                </p>
                <div className="flex flex-col gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                  {allCategories.length === 0 ? (
                    <p className="text-xs text-gray-500">No categories yet</p>
                  ) : (
                    allCategories.map((cat) => (
                      <label
                        key={cat._id}
                        className="inline-flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={(categoryIds || []).includes(cat._id)}
                          onChange={(e) => {
                            const current = categoryIds || [];
                            const next = e.target.checked
                              ? [...current.filter((c) => c !== cat._id), cat._id]
                              : current.filter((c) => c !== cat._id);
                            setCategoryIds(next);
                          }}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          {cat.name}
                          {cat.slug && (
                            <span className="text-gray-400 ml-1">
                              ({cat.slug})
                            </span>
                          )}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Country targeting */}
            {typeof setCountryCodes === "function" && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Country targeting
                </label>
                <p className="text-xs text-gray-500">
                  Empty = all countries
                </p>
                <div className="flex flex-wrap gap-6">
                  <label className="inline-flex items-center gap-3 cursor-pointer">
                    <span
                      className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 bg-gray-200"
                      style={{ backgroundColor: (countryCodes || []).includes("US") ? "#2563eb" : undefined }}
                    >
                      <input
                        type="checkbox"
                        checked={(countryCodes || []).includes("US")}
                        onChange={(e) => {
                          const current = countryCodes || [];
                          const next = e.target.checked
                            ? [...current.filter((c) => c !== "US"), "US"]
                            : current.filter((c) => c !== "US");
                          setCountryCodes(next);
                        }}
                        className="sr-only"
                      />
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          (countryCodes || []).includes("US") ? "translate-x-5" : "translate-x-0.5"
                        }`}
                      />
                    </span>
                    <span className="text-sm text-gray-700 inline-flex items-center gap-1.5">
                        <img src="https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg" className="w-4 h-4" alt="US" /> US
                      </span>
                  </label>
                  <label className="inline-flex items-center gap-3 cursor-pointer">
                    <span
                      className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 bg-gray-200"
                      style={{ backgroundColor: (countryCodes || []).includes("IN") ? "#2563eb" : undefined }}
                    >
                      <input
                        type="checkbox"
                        checked={(countryCodes || []).includes("IN")}
                        onChange={(e) => {
                          const current = countryCodes || [];
                          const next = e.target.checked
                            ? [...current.filter((c) => c !== "IN"), "IN"]
                            : current.filter((c) => c !== "IN");
                          setCountryCodes(next);
                        }}
                        className="sr-only"
                      />
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          (countryCodes || []).includes("IN") ? "translate-x-5" : "translate-x-0.5"
                        }`}
                      />
                    </span>
                    <span className="text-sm text-gray-700 inline-flex items-center gap-1.5">
                        <img src="https://upload.wikimedia.org/wikipedia/en/4/41/Flag_of_India.svg" className="w-4 h-4" alt="IN" /> IN
                      </span>
                  </label>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="space-y-2">
              <button
                onClick={handleSave}
                disabled={!isReady}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
                title="Save content"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                  />
                </svg>
                Save Blog & Settings
              </button>
              <span className="text-xs text-gray-500">
                Press to save the blog and settings
              </span>
              {/* {hasChanges && (
                <div className="flex items-center gap-2 text-amber-600 text-sm">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Unsaved changes</span>
                </div>
              )} */}
            </div>

            {/* Status Indicators */}
            {/* <div className="pt-4 border-t border-gray-200 space-y-3">
              {isReady && (
                <div className="flex items-center gap-2 text-green-600">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium">Editor Ready</span>
                </div>
              )}

              <div className="text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Auto-saves every 30s
                </div>
              </div>
            </div> */}
          </div>
        </div>
      )}

      {/* Right Side - Main Content */}
      <div className="flex-1 flex flex-col max-h-screen overflow-hidden">
        {/* Toolbar */}
        {showControls && (
          <div className="bg-white border-b border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">
                Content Tools
              </h3>
              <div className="text-xs text-gray-500">
                Click to insert content blocks
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Text Formatting */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Text:</span>
                <button
                  onClick={handleList}
                  disabled={!isReady}
                  className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  title="Insert List"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                  List
                </button>
              </div>

              {/* Media */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">
                  Media:
                </span>
                <button
                  onClick={handleImage}
                  disabled={!isReady}
                  className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  title="Insert Image"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Image
                </button>
              </div>

              {/* Data */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Data:</span>
                <button
                  onClick={handleTable}
                  disabled={!isReady}
                  className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  title="Insert Table"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 10h18M3 14h18m-9-4v8m-7 0V4a1 1 0 011-1h16a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1z"
                    />
                  </svg>
                  Table
                </button>
              </div>

              {/* CTA */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">CTA:</span>
                <button
                  onClick={handleCTA}
                  disabled={!isReady}
                  className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  title="Insert CTA Button"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  CTA
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Editor */}
        <div className="flex-1 p-4 overflow-hidden">
          <div className="editor-container w-full h-full border border-gray-300 rounded-md overflow-auto">
            <div
              ref={containerRef}
              id="editorjs-container"
              className="w-full min-h-full"
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}
